import HeroImage from '@/components/HeroImage';
import { CATEGORIES_ICONS } from '@/constants/category';
import useCaptureListData from '@/hooks/useCaptureListData';
import { EditableGlassCard } from '@/pages/CategorySpotlight/components/GlassCard';
import { cleanCategoryForMutation } from '@/services/categories';
import { useUpdateCategoryMutation } from '@/services/categories/hooks';
import { useCategories } from '@/services/categories/hooks';
import { useActionsCategories } from '@/stores/useActionsCategories';
import { IconChevronDoubleLeft, IconChevronDoubleRight } from '@/theme/icons';
import { Category } from '@/types/category';
import { refetchCategoryAndBlocks } from '@/utils/category';
import { fixUncategorizedName } from '@/utils/index';
import rem from '@/utils/rem';
import {
  Box,
  Editable,
  EditablePreview,
  EditableTextarea,
  Flex,
  Grid,
  GridItem,
  Heading,
  IconButton,
  Spinner,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Icon } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

interface HeroSectionProps {
  category: Category;
  onImageLoad?: () => void;
}

const createM7FormSchema = (t: (key: string) => string) =>
  z.object({
    ultimateVision: z
      .string()
      .trim()
      .max(10000, { message: t('sections.magnificent7.validation.ultimateVision.maxLength') })
      .nullable()
      .optional(),
    role: z
      .string()
      .trim()
      .max(255, { message: t('sections.magnificent7.validation.role.maxLength') })
      .nullable()
      .optional(),
    ultimatePurpose: z
      .string()
      .trim()
      .max(10000, { message: t('sections.magnificent7.validation.ultimatePurpose.maxLength') })
      .optional(),
    oneYearGoal: z
      .string()
      .trim()
      .max(10000, { message: t('sections.magnificent7.validation.oneYearGoal.maxLength') })
      .nullable()
      .optional(),
    ninetyDayGoal: z
      .string()
      .trim()
      .max(10000, { message: t('sections.magnificent7.validation.ninetyDayGoal.maxLength') })
      .nullable()
      .optional(),
  });

type FormData = z.infer<ReturnType<typeof createM7FormSchema>>;

const HeroSection: React.FC<HeroSectionProps> = ({ category, onImageLoad }) => {
  const { t } = useTranslation('CategorySpotlight');
  const { t: tCategoryDetails } = useTranslation('CategoryManagerDetails');
  const M7FormSchema = createM7FormSchema(tCategoryDetails);
  const toast = useToast();

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [dynamicHeight, setDynamicHeight] = useState<string>('75vh');
  const { categories } = useCaptureListData();
  const { isLoading: isCategoriesLoading } = useCategories();
  const selectedCategory = useActionsCategories((state) => state.selectedCategory);
  const updateSelectedCategory = useActionsCategories((state) => state.updateSelectedCategory);

  const updateCategory = useUpdateCategoryMutation();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      ultimateVision: category?.ultimateVision ?? null,
      role: category?.role ?? null,
      ultimatePurpose: category?.ultimatePurpose ?? '',
      oneYearGoal: category?.oneYearGoal ?? null,
      ninetyDayGoal: category?.ninetyDayGoal ?? null,
    },
    resolver: zodResolver(M7FormSchema),
    mode: 'onChange',
  });

  // Refs to measure content height more precisely
  const contentRef = useRef<HTMLDivElement>(null);
  const cardsContainerRef = useRef<HTMLDivElement>(null);

  const filteredCategories = useMemo(() => {
    return categories?.filter((c) => !c.isArchived) || [];
  }, [categories]);

  const handleTogglePanel = useCallback(() => {
    setIsPanelOpen(!isPanelOpen);
  }, [isPanelOpen]);

  const handleCategorySelect = useCallback(
    (selectedCat: Category) => {
      updateSelectedCategory(selectedCat);
      setIsPanelOpen(false); // Close panel after selection
    },
    [updateSelectedCategory],
  );

  const onSubmit = useCallback(
    (data: FormData) => {
      if (!category) {
        return;
      }

      updateCategory.mutate(
        {
          ...cleanCategoryForMutation(category),
          ultimateVision: data?.ultimateVision ?? null,
          role: data?.role ?? null,
          ultimatePurpose: data?.ultimatePurpose ?? '',
          oneYearGoal: data?.oneYearGoal ?? null,
          ninetyDayGoal: data?.ninetyDayGoal ?? null,
        },
        {
          onSuccess: () => {
            refetchCategoryAndBlocks();

            toast({
              title: tCategoryDetails('sections.magnificent7.toasts.saved'),
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          },

          onError: (error) => {
            toast({
              title: error?.response?.errors?.[0]?.message,
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
          },
        },
      );
    },
    [category, toast, updateCategory, tCategoryDetails],
  );

  const handleBlur = useCallback(
    (value: string | undefined | null, prevValue: string, field: keyof FormData) => {
      if (value === undefined) {
        setValue(field, prevValue);
      }
    },
    [setValue],
  );

  // Calculate dynamic height based on content
  const calculateDynamicHeight = useCallback(() => {
    // Try to measure the cards container first, fallback to full content
    const measureElement = cardsContainerRef.current || contentRef.current;

    if (measureElement) {
      const contentHeight = measureElement.offsetHeight;
      const windowHeight = window.innerHeight;

      // If measuring cards container, add padding for the grid container
      // If measuring full content, subtract space for bottom sections
      const adjustedHeight =
        measureElement === cardsContainerRef.current
          ? contentHeight + 200 // Add padding for cards container measurement
          : contentHeight - 200; // Subtract space for bottom sections

      const minHeight = Math.max(adjustedHeight, windowHeight * 0.65);
      const heightInVh = (minHeight / windowHeight) * 100;

      setDynamicHeight(`${Math.ceil(heightInVh)}vh`);
    }
  }, []);

  // Effect to calculate height when content changes
  useEffect(() => {
    const timer = setTimeout(() => {
      calculateDynamicHeight();
    }, 100);

    return () => clearTimeout(timer);
  }, [
    calculateDynamicHeight,
    category.ultimateVision,
    category.ultimatePurpose,
    category.oneYearGoal,
    category.ninetyDayGoal,
  ]);

  // Effect to recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      setTimeout(calculateDynamicHeight, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateDynamicHeight]);

  // Effect to recalculate when panel state changes
  useEffect(() => {
    setTimeout(calculateDynamicHeight, 300); // Wait for panel animation to complete
  }, [isPanelOpen, calculateDynamicHeight]);

  // Reset form when category changes
  useEffect(() => {
    reset({
      ultimateVision: category?.ultimateVision ?? null,
      role: category?.role ?? null,
      ultimatePurpose: category?.ultimatePurpose ?? '',
      oneYearGoal: category?.oneYearGoal ?? null,
      ninetyDayGoal: category?.ninetyDayGoal ?? null,
    });
  }, [
    category?.ninetyDayGoal,
    category?.oneYearGoal,
    category?.role,
    category?.ultimatePurpose,
    category?.ultimateVision,
    reset,
  ]);

  return (
    <HeroImage
      category={category}
      imageUrl={category.selectedImageUrl}
      fadeIn
      noCategoryGradient
      dynamicHeight={dynamicHeight}
      onImageLoad={onImageLoad}
      fromCategorySpotlight
    >
      <>
        {/* Sliding Categories Panel */}
        <Box
          position="fixed"
          zIndex={30}
          top={0}
          bottom={0}
          left={0}
          overflow="hidden"
          width={isPanelOpen ? rem(320) : rem(25)}
          transition="width 0.3s ease-in-out"
          backgroundColor="background-primary"
        >
          {isPanelOpen && (
            <VStack
              alignItems="center"
              overflowY="auto"
              height="full"
              padding={rem(16)}
              paddingTop={rem(60)}
              spacing={rem(24)}
            >
              <Box flexShrink={0} width={rem(240)}>
                <Text
                  overflow="hidden"
                  color="text-primary"
                  fontSize="sm"
                  fontWeight="bold"
                  letterSpacing="0.5px"
                  textAlign="left"
                  textTransform="uppercase"
                  whiteSpace="nowrap"
                >
                  {t('sidebar.selectCategory')}
                </Text>
              </Box>

              {isCategoriesLoading ? (
                <Flex justifyContent="center" padding={rem(32)}>
                  <Spinner color="text-primary" />
                </Flex>
              ) : (
                <VStack alignItems="center" width="full" spacing={rem(12)}>
                  {filteredCategories
                    .filter((cat) => cat.name !== 'Uncategorized')
                    .map((cat) => {
                      const isSelected = selectedCategory?.id === cat?.id;
                      const CategoryIcon = CATEGORIES_ICONS?.[cat.icon ?? 'uncategorized'];

                      return (
                        <Flex
                          key={cat.id}
                          alignItems="center"
                          justifyContent="flex-start"
                          width="100%"
                          maxWidth={rem(240)}
                          height={rem(36)}
                          opacity={isSelected ? 1 : 0.4}
                          borderRadius={rem(4)}
                          cursor="pointer"
                          backgroundColor={cat.color}
                          onClick={() => handleCategorySelect(cat)}
                          paddingX={rem(16)}
                          paddingY={rem(8)}
                        >
                          <Flex
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                            width={rem(20)}
                            height={rem(20)}
                            marginRight={rem(12)}
                          >
                            <Icon as={CategoryIcon} boxSize={rem(14)} color="black" stroke="black" />
                          </Flex>

                          <Text
                            flex="1"
                            color="black"
                            fontSize="sm"
                            fontWeight={900}
                            textTransform="uppercase"
                            noOfLines={1}
                          >
                            {fixUncategorizedName(cat.name)}
                          </Text>
                        </Flex>
                      );
                    })}
                </VStack>
              )}
            </VStack>
          )}
        </Box>

        {/* White border line */}
        <Box
          position="fixed"
          zIndex={35}
          top={0}
          bottom={0}
          left={isPanelOpen ? rem(320) : rem(25)}
          width="1px"
          transition="left 0.3s ease-in-out"
          backgroundColor="white"
        />

        {/* Circular button with >> crossing the white border */}
        <Flex
          position="fixed"
          zIndex={40}
          top="50%"
          left={isPanelOpen ? rem(306) : rem(11)}
          alignItems="center"
          justifyContent="center"
          transform="translateY(-50%)"
          transition="left 0.3s ease-in-out"
        >
          <IconButton
            width={rem(30)}
            minWidth={rem(30)}
            height={rem(30)}
            padding={rem(4)}
            color="white"
            border="1px solid white"
            borderRadius="full"
            _hover={{
              backgroundColor: 'blue.1000',
            }}
            _active={{
              backgroundColor: 'blue.1000',
            }}
            aria-label={isPanelOpen ? t('sidebar.closeCategories') : t('sidebar.openCategories')}
            backgroundColor="blue.1000"
            onClick={handleTogglePanel}
            size="lg"
          >
            <Icon as={isPanelOpen ? IconChevronDoubleLeft : IconChevronDoubleRight} boxSize={rem(20)} color="white" />
          </IconButton>
        </Flex>

        <Grid
          ref={contentRef}
          alignItems="center"
          gridGap={{ base: rem(32), md: rem(48), lg: rem(48), '3xl': rem(48) }}
          gridTemplateRows={{ base: 'auto auto', md: 'auto auto', lg: 'auto auto', '3xl': '1fr' }}
          gridTemplateColumns={{
            base: '1fr',
            md: '1fr',
            lg: 'minmax(0, 1fr) minmax(0, 1fr)',
            '3xl': 'minmax(0, 1fr) minmax(0, 1fr)',
          }}
          overflow="hidden"
          width="full"
          minHeight="75vh"
          paddingRight={{ base: 0, md: 0, lg: rem(32), '3xl': rem(32) }}
          paddingX={{ base: 0, md: 0, lg: rem(32), '3xl': rem(32) }}
          paddingY={{ base: rem(32), md: rem(48), lg: rem(64), '3xl': rem(64) }}
        >
          <GridItem alignItems="center" justifyContent="center" display="flex" minWidth={0} textAlign="center">
            <Box
              sx={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '2px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: '2px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: 'rgba(255, 255, 255, 0.5)',
                },
              }}
              overflowY="auto"
              width="full"
              maxWidth="100%"
              maxHeight={{ base: rem(300), lg: rem(400) }}
              paddingX={rem(8)}
            >
              <VStack
                alignItems={{ base: 'center', md: 'center', lg: 'flex-start' }}
                width="full"
                paddingY={rem(8)}
                spacing={rem(12)}
              >
                <Heading
                  sx={{
                    hyphens: 'auto',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                  }}
                  zIndex={10}
                  color="text-primary"
                  fontFamily="Barlow Condensed"
                  fontSize="52px"
                  fontWeight="600"
                  lineHeight="100%"
                  textAlign="left"
                  fontStyle="normal"
                  textTransform="uppercase"
                  wordBreak="break-word"
                >
                  {fixUncategorizedName(category.name)}
                </Heading>

                <Controller
                  name="role"
                  control={control}
                  render={({ field: { ref, value, onChange } }) => (
                    <Editable
                      ref={ref}
                      sx={{
                        hyphens: 'auto',
                        leadingTrim: 'both',
                        textEdge: 'cap',
                      }}
                      zIndex={10}
                      display="block"
                      overflow="unset"
                      width="full"
                      height="full"
                      color="text-primary"
                      fontFamily="Satoshi"
                      fontSize="24px"
                      fontWeight="500"
                      lineHeight="100%"
                      textAlign={{ base: 'center', md: 'center', lg: 'left', '3xl': 'left' }}
                      fontStyle="normal"
                      textTransform="uppercase"
                      borderRadius={rem(5)}
                      wordBreak="break-word"
                      defaultValue={value ?? ''}
                      isDisabled={isSubmitting}
                      onBlur={(prevValue) => handleBlur(value, prevValue, 'role')}
                      onChange={onChange}
                      onSubmit={() => {
                        if (category?.role !== value && (!!value || !!category?.role)) {
                          handleSubmit(onSubmit)();
                        }
                      }}
                      placeholder={t('hero.placeholders.role')}
                      selectAllOnFocus={false}
                      submitOnBlur
                      value={value ?? ''}
                    >
                      <EditablePreview
                        width="full"
                        height="auto"
                        minHeight={rem(60)}
                        fontStyle={value ? 'normal' : 'italic'}
                        opacity={value ? 1 : 0.5}
                      />
                      <EditableTextarea
                        height="auto"
                        minHeight={rem(60)}
                        border="none"
                        _focusVisible={{
                          boxShadow: 'none',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        }}
                        _placeholder={{
                          fontStyle: 'italic',
                        }}
                        resize="none"
                        backgroundColor="transparent"
                        onFocus={(e) =>
                          e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)
                        }
                        rows={3}
                      />
                    </Editable>
                  )}
                />
                {errors?.role?.message && (
                  <Text as="span" color="red.300" fontSize="small">
                    {errors?.role?.message as string}
                  </Text>
                )}
              </VStack>
            </Box>
          </GridItem>

          <GridItem alignItems={{ base: 'center', lg: 'flex-start' }} justifyContent="center" display="flex">
            <VStack ref={cardsContainerRef} zIndex={10} alignItems="center" width="full" spacing={rem(10)}>
              <Text
                alignSelf="flex-start"
                width="full"
                color="text-primary"
                fontSize={{ base: 'lg', md: 'xl' }}
                fontWeight="bold"
                textAlign="left"
                textTransform="uppercase"
                textShadow="1px 1px 2px rgba(0,0,0,0.5)"
              >
                {t('hero.bigPicture')}
              </Text>

              <EditableGlassCard
                title={t('hero.ultimateVision')}
                control={control}
                name="ultimateVision"
                placeholder={t('hero.placeholders.ultimateVision')}
                categoryColor={category.color}
                fitContent={false}
                isSubmitting={isSubmitting}
                errors={errors}
                onBlur={(prevValue: string, value: string | undefined) =>
                  handleBlur(value, prevValue, 'ultimateVision')
                }
                onSubmit={(value: string | undefined) => {
                  if (category?.ultimateVision !== value && value !== undefined) {
                    handleSubmit(onSubmit)();
                  }
                }}
              />

              <EditableGlassCard
                title={t('hero.ultimatePurpose')}
                control={control}
                name="ultimatePurpose"
                placeholder={t('hero.placeholders.ultimatePurpose')}
                categoryColor={category.color}
                fitContent={false}
                isSubmitting={isSubmitting}
                errors={errors}
                onBlur={(prevValue: string, value: string | undefined) =>
                  handleBlur(value, prevValue, 'ultimatePurpose')
                }
                onSubmit={(value: string | undefined) => {
                  if (category?.ultimatePurpose !== value && (!!value || !!category?.ultimatePurpose)) {
                    handleSubmit(onSubmit)();
                  }
                }}
              />

              {/* Goal Cards Container with CSS Grid for height synchronization */}
              <Grid
                alignItems="stretch"
                gridGap={rem(10)}
                gridTemplateColumns={{
                  base: '1fr',
                  md: '1fr',
                  lg: '1fr 1fr',
                  '3xl': '1fr 1fr',
                }}
                width="full"
              >
                <EditableGlassCard
                  title={t('hero.oneYearGoal')}
                  control={control}
                  name="oneYearGoal"
                  placeholder={t('hero.placeholders.oneYearGoal')}
                  categoryColor={category.color}
                  width="full"
                  fitContent={false}
                  isSubmitting={isSubmitting}
                  errors={errors}
                  onBlur={(prevValue: string, value: string | undefined) => handleBlur(value, prevValue, 'oneYearGoal')}
                  onSubmit={(value: string | undefined) => {
                    if (category?.oneYearGoal !== value && (!!value || !!category?.oneYearGoal)) {
                      handleSubmit(onSubmit)();
                    }
                  }}
                />

                <EditableGlassCard
                  title={t('hero.ninetyDayGoal')}
                  control={control}
                  name="ninetyDayGoal"
                  placeholder={t('hero.placeholders.ninetyDayGoal')}
                  categoryColor={category.color}
                  width="full"
                  fitContent={false}
                  isSubmitting={isSubmitting}
                  errors={errors}
                  onBlur={(prevValue: string, value: string | undefined) =>
                    handleBlur(value, prevValue, 'ninetyDayGoal')
                  }
                  onSubmit={(value: string | undefined) => {
                    if (category?.ninetyDayGoal !== value && (!!value || !!category?.ninetyDayGoal)) {
                      handleSubmit(onSubmit)();
                    }
                  }}
                />
              </Grid>
            </VStack>
          </GridItem>
        </Grid>
      </>
    </HeroImage>
  );
};

export default HeroSection;
